import { FC, useState } from 'react';
import { BackIcon } from 'assets/icons';
import LineItemPlusReactIcon from 'assets/react-icons/LineItemPlusReactIcon';
import { CustomButton, UploadAttachmentToPresignedModal } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import FormCloseReactIcon from 'assets/react-icons/FormCloseReactIcon';
import { isEqual } from 'lodash';
import { PRESIGNED_UPLOAD_USE_CASE } from 'constants/common';
import { UploadAttachmentFile } from 'types/baseTypes';

interface AttachmentViewDetailsPanelProps {
  estimateAttachments: any;
  onBackClick: () => void;
  onSave: (attachment: any[]) => void;
  canEdit: boolean;
}

const AttachmentViewDetailsPanel: FC<AttachmentViewDetailsPanelProps> = ({
  estimateAttachments,
  onBackClick,
  onSave,
  canEdit
}) => {
  const [attachments, setAttachments] = useState(estimateAttachments);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);

  const handleAddPhotoClick = () => {
    setIsAttachmentModalOpen(true);
  };

  const resetAttachmentModalState = () => {
    setIsAttachmentModalOpen(false);
  };

  const handlePresignedSave = (uploadedFiles: UploadAttachmentFile[]) => {
    const newAttachments = uploadedFiles.map(file => ({
      title: file.title,
      url: file.url,
      imagePreview: file.previewData ?? file.urlLink,
      id: null,
      urlLink: file.urlLink
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
  };
  const onRemoveAttachment = attachment => {
    setAttachments(prev => prev.filter(att => att.url !== attachment.url));
  };

  const onSaveClick = async () => {
    const updatedAttachments = attachments.map(({ title, url }) => ({ title, url }));
    onSave(updatedAttachments);
  };

  const areAttachmentsDifferent = () => {
    return !isEqual(attachments, estimateAttachments);
  };

  return (
    <div className="h-full w-96">
      <div className="mb-1 flex items-center border-b border-secondaryBtn py-4">
        <BackIcon className="mx-4 cursor-pointer" id="estimate-details-panel-back-arrow" onClick={onBackClick} />
        <div className="text-17px font-semibold text-headingGray">Attachments</div>
      </div>
      <div className="py-6 px-4">
        {canEdit && (
          <div
            onClick={handleAddPhotoClick}
            className="mb-5 flex w-full cursor-pointer flex-row rounded border border-borderGray py-4 px-5">
            <div className="flex flex-row items-center gap-2">
              <LineItemPlusReactIcon id="create-estimate-add-photo-icon" className="shrink-0" />
              <div id="estimate-add-photo" className="text-17px font-semibold text-secondary">
                Attach another photos
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-col gap-5">
          {attachments?.map(attachment => (
            <div
              key={attachment.url}
              className="relative flex w-full flex-row 
            items-center rounded border border-borderGray p-3 last:mb-5">
              <div className="relative mr-4 h-12 w-12 rounded-md">
                <img
                  className="absolute left-1/2 h-full -translate-x-1/2 rounded-md object-cover"
                  src={attachment.imagePreview || attachment.urlLink}
                  alt={attachment.title}
                />
              </div>
              <div className="mb-[6px] break-words text-17px font-semibold text-heading line-clamp-2">
                {attachment.title}
              </div>
              {canEdit && (
                <div className="ml-auto">
                  <FormCloseReactIcon
                    id="create-estimate-remove-attachment-close-icon"
                    onClick={e => {
                      e.stopPropagation();
                      onRemoveAttachment(attachment);
                    }}
                    className="cursor-pointer"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        {isAttachmentModalOpen && (
          <UploadAttachmentToPresignedModal
            open={isAttachmentModalOpen}
            toggleOpen={resetAttachmentModalState}
            onSave={handlePresignedSave}
            useCase={PRESIGNED_UPLOAD_USE_CASE.ESTIMATES}
            idPrefix="estimate-view-attachment-upload"
          />
        )}
      </div>
      <div className="flex w-full flex-col gap-y-4 p-4 pt-0">
        <CustomButton
          id="edit-estimate-tnc-save-button"
          className="w-full"
          onClick={onSaveClick}
          type={ButtonType.PRIMARY}
          disabled={!areAttachmentsDifferent()}>
          Save
        </CustomButton>
        <CustomButton
          id="edit-estimate-tnc-cancel-button"
          className="w-full"
          onClick={onBackClick}
          type={ButtonType.SECONDARY}>
          Cancel
        </CustomButton>
      </div>
    </div>
  );
};

export default AttachmentViewDetailsPanel;
